import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './Admin.css';

const ADMIN_IDS = ['bfe4ddd4-9cd1-4e31-9c7f-069a67aed170'];
const TABS = ['Stats', 'Listings', 'Users', 'Messages', 'Bike of the Week'];
const CAT_LABELS = { street_legal:'Street Legal', mini_moto:'Mini Moto', medium_moto:'Medium Moto', mega_moto:'Mega Moto' };

function getWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Stats');
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [botwSubs, setBotwSubs] = useState([]);
  const [botwVotes, setBotwVotes] = useState([]);
  const [botwLoading, setBotwLoading] = useState(false);
  const [winner, setWinner] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (!ADMIN_IDS.includes(user.id)) { navigate('/'); return; }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || !ADMIN_IDS.includes(user.id)) return;
    if (tab === 'Stats')            loadStats();
    if (tab === 'Listings')         loadListings();
    if (tab === 'Users')            loadUsers();
    if (tab === 'Messages')         loadMessages();
    if (tab === 'Bike of the Week') loadBotw();
  }, [tab, user]);

  async function loadStats() {
    const [{ count: totalListings },{ count: activeListings },{ count: totalUsers },{ count: totalMessages }] = await Promise.all([
      supabase.from('listings').select('*', { count: 'exact', head: true }),
      supabase.from('listings').select('*', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
      supabase.from('messages').select('*', { count: 'exact', head: true }),
    ]);
    const { data: topViewed } = await supabase.from('listings').select('id, title, views, price').order('views', { ascending: false }).limit(5);
    setStats({ totalListings, activeListings, totalUsers, totalMessages, topViewed });
  }

  async function loadListings() {
    setListingsLoading(true);
    const { data } = await supabase.from('listings').select('id, title, price, is_active, created_at, views, seller_id, city, state, condition').order('created_at', { ascending: false }).limit(100);
    setListings(data || []);
    setListingsLoading(false);
  }

  async function loadUsers() {
    setUsersLoading(true);
    const { data } = await supabase.from('profiles').select('id, username, full_name, created_at, is_banned, avatar_url').order('created_at', { ascending: false }).limit(100);
    setUsers(data || []);
    setUsersLoading(false);
  }

  async function loadMessages() {
    setMessagesLoading(true);
    const { data } = await supabase.from('messages').select('id, content, created_at, sender_id, receiver_id, listing_id').order('created_at', { ascending: false }).limit(100);
    setMessages(data || []);
    setMessagesLoading(false);
  }

  async function loadBotw() {
    setBotwLoading(true);
    const weekStart = getWeekStart();
    const { data: subs } = await supabase.from('botw_submissions').select('*, botw_votes(count)').gte('created_at', weekStart.toISOString()).order('created_at', { ascending: false });
    const { data: votes } = await supabase.from('botw_votes').select('id, submission_id, user_id, created_at').gte('created_at', weekStart.toISOString()).order('created_at', { ascending: false });
    setBotwSubs(subs || []);
    setBotwVotes(votes || []);
    setBotwLoading(false);
  }

  function notify(msg) { setActionMsg(msg); setTimeout(() => setActionMsg(''), 4000); }

  async function deleteBotwSub(id) {
    if (!window.confirm('Delete this submission and all its votes?')) return;
    const { error } = await supabase.from('botw_submissions').delete().eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setBotwSubs(p => p.filter(s => s.id !== id));
    notify('Submission deleted.');
  }

  async function deleteBotwVote(id) {
    if (!window.confirm('Delete this vote?')) return;
    const { error } = await supabase.from('botw_votes').delete().eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setBotwVotes(p => p.filter(v => v.id !== id));
    notify('Vote deleted.');
  }

  function pickWinner(sub) {
    if (!window.confirm(`Pick "${sub.bike_name}" by ${sub.rider_name} as this week's winner?`)) return;
    setWinner(sub);
    notify(`🏆 Winner set: ${sub.bike_name} by ${sub.rider_name}`);
  }

  async function resetWeek() {
    if (!window.confirm('Reset ALL submissions and votes for this week? This cannot be undone!')) return;
    if (!window.confirm('Are you absolutely sure?')) return;
    const weekStart = getWeekStart();
    const { error } = await supabase.from('botw_submissions').delete().gte('created_at', weekStart.toISOString());
    if (error) { notify('Error: ' + error.message); return; }
    setBotwSubs([]); setBotwVotes([]); setWinner(null);
    notify('Week reset. All submissions and votes deleted.');
  }

  async function deleteListing(id) {
    if (!window.confirm('Delete this listing permanently?')) return;
    const { error } = await supabase.from('listings').delete().eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setListings(p => p.filter(l => l.id !== id));
    notify('Listing deleted.');
  }

  async function toggleListingActive(id, current) {
    const { error } = await supabase.from('listings').update({ is_active: !current }).eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setListings(p => p.map(l => l.id === id ? { ...l, is_active: !current } : l));
    notify(current ? 'Listing deactivated.' : 'Listing activated.');
  }

  async function toggleBanUser(id, current) {
    if (!window.confirm(current ? 'Unban this user?' : 'Ban this user?')) return;
    const { error } = await supabase.from('profiles').update({ is_banned: !current }).eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setUsers(p => p.map(u => u.id === id ? { ...u, is_banned: !current } : u));
    notify(current ? 'User unbanned.' : 'User banned.');
  }

  async function deleteMessage(id) {
    if (!window.confirm('Delete this message?')) return;
    const { error } = await supabase.from('messages').delete().eq('id', id);
    if (error) { notify('Error: ' + error.message); return; }
    setMessages(p => p.filter(m => m.id !== id));
    notify('Message deleted.');
  }

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!user || !ADMIN_IDS.includes(user.id)) return null;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-inner">
          <div>
            <span className="admin-badge">ADMIN</span>
            <h1 className="admin-title">Control Panel</h1>
          </div>
          <span className="admin-user">{user.email}</span>
        </div>
      </div>
      <div className="admin-body">
        {actionMsg && <div className="admin-toast">{actionMsg}</div>}
        <div className="admin-tabs">
          {TABS.map(t => (
            <button key={t} className={`admin-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        {tab === 'Stats' && (
          <div className="admin-section">
            {!stats ? <div className="admin-loading">Loading stats...</div> : (
              <>
                <div className="stats-grid">
                  <div className="stat-card"><span className="stat-value">{stats.totalListings ?? '—'}</span><span className="stat-label">Total Listings</span></div>
                  <div className="stat-card"><span className="stat-value">{stats.activeListings ?? '—'}</span><span className="stat-label">Active Listings</span></div>
                  <div className="stat-card"><span className="stat-value">{stats.totalUsers ?? '—'}</span><span className="stat-label">Total Users</span></div>
                  <div className="stat-card"><span className="stat-value">{stats.totalMessages ?? '—'}</span><span className="stat-label">Total Messages</span></div>
                </div>
                <div className="admin-table-wrap" style={{ marginTop: 32 }}>
                  <h3 className="admin-sub">Top Viewed Listings</h3>
                  <table className="admin-table">
                    <thead><tr><th>Title</th><th>Price</th><th>Views</th></tr></thead>
                    <tbody>{(stats.topViewed || []).map(l => (<tr key={l.id}><td>{l.title}</td><td>${Number(l.price).toLocaleString()}</td><td>{l.views ?? 0}</td></tr>))}</tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'Listings' && (
          <div className="admin-section">
            <div className="admin-table-wrap">
              {listingsLoading ? <div className="admin-loading">Loading listings...</div> : (
                <table className="admin-table">
                  <thead><tr><th>Title</th><th>Price</th><th>Location</th><th>Views</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                  <tbody>
                    {listings.map(l => (
                      <tr key={l.id}>
                        <td className="td-title"><a href={`/listings/${l.id}`} target="_blank" rel="noreferrer">{l.title}</a></td>
                        <td>${Number(l.price).toLocaleString()}</td>
                        <td>{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                        <td>{l.views ?? 0}</td>
                        <td><span className={`admin-pill ${l.is_active ? 'pill-active' : 'pill-inactive'}`}>{l.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>{new Date(l.created_at).toLocaleDateString()}</td>
                        <td className="td-actions">
                          <button className="admin-btn admin-btn-ghost" onClick={() => toggleListingActive(l.id, l.is_active)}>{l.is_active ? 'Deactivate' : 'Activate'}</button>
                          <button className="admin-btn admin-btn-danger" onClick={() => deleteListing(l.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'Users' && (
          <div className="admin-section">
            <div className="admin-table-wrap">
              {usersLoading ? <div className="admin-loading">Loading users...</div> : (
                <table className="admin-table">
                  <thead><tr><th>Username</th><th>Full Name</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td><strong>{u.username || '—'}</strong></td>
                        <td>{u.full_name || '—'}</td>
                        <td><span className={`admin-pill ${u.is_banned ? 'pill-banned' : 'pill-active'}`}>{u.is_banned ? 'Banned' : 'Active'}</span></td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="td-actions">
                          <button className={`admin-btn ${u.is_banned ? 'admin-btn-ghost' : 'admin-btn-danger'}`} onClick={() => toggleBanUser(u.id, u.is_banned)} disabled={ADMIN_IDS.includes(u.id)}>
                            {u.is_banned ? 'Unban' : 'Ban'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'Messages' && (
          <div className="admin-section">
            <div className="admin-table-wrap">
              {messagesLoading ? <div className="admin-loading">Loading messages...</div> : (
                <table className="admin-table">
                  <thead><tr><th>Message</th><th>Listing</th><th>Sent</th><th>Actions</th></tr></thead>
                  <tbody>
                    {messages.map(m => (
                      <tr key={m.id}>
                        <td className="td-message">{m.content}</td>
                        <td>{m.listing_id ? <a href={`/listings/${m.listing_id}`} target="_blank" rel="noreferrer">View</a> : '—'}</td>
                        <td>{new Date(m.created_at).toLocaleDateString()}</td>
                        <td className="td-actions"><button className="admin-btn admin-btn-danger" onClick={() => deleteMessage(m.id)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'Bike of the Week' && (
          <div className="admin-section">
            {botwLoading ? <div className="admin-loading">Loading...</div> : (
              <>
                {winner && (
                  <div className="botw-winner-banner">
                    🏆 <strong>This week's winner:</strong> {winner.bike_name} by {winner.rider_name}
                    <button className="admin-btn admin-btn-ghost" style={{marginLeft:12}} onClick={() => setWinner(null)}>Clear</button>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                  <button className="admin-btn admin-btn-danger" onClick={resetWeek}>🗑 Reset This Week</button>
                </div>
                <h3 className="admin-sub">Submissions this week ({botwSubs.length})</h3>
                <div className="admin-table-wrap" style={{ marginBottom: 32 }}>
                  <table className="admin-table">
                    <thead><tr><th>Photo</th><th>Bike</th><th>Rider</th><th>Category</th><th>Stage</th><th>Votes</th><th>Submitted</th><th>Actions</th></tr></thead>
                    <tbody>
                      {botwSubs.length === 0 ? (
                        <tr><td colSpan={8} style={{textAlign:'center', padding:24, color:'var(--text-muted)'}}>No submissions this week</td></tr>
                      ) : botwSubs.map(s => (
                        <tr key={s.id}>
                          <td>{s.image_url ? <img src={s.image_url} alt="" style={{width:48, height:48, objectFit:'cover', borderRadius:6}} /> : '—'}</td>
                          <td><strong>{s.bike_name}</strong></td>
                          <td>{s.rider_name}</td>
                          <td>{CAT_LABELS[s.category] || s.category}</td>
                          <td>Stage {s.stage}</td>
                          <td>{s.botw_votes?.[0]?.count || 0}</td>
                          <td>{new Date(s.created_at).toLocaleDateString()}</td>
                          <td className="td-actions">
                            <button className="admin-btn admin-btn-ghost" onClick={() => pickWinner(s)}>🏆 Pick</button>
                            <button className="admin-btn admin-btn-danger" onClick={() => deleteBotwSub(s.id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3 className="admin-sub">Votes this week ({botwVotes.length})</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr><th>Submission</th><th>User</th><th>Voted At</th><th>Actions</th></tr></thead>
                    <tbody>
                      {botwVotes.length === 0 ? (
                        <tr><td colSpan={4} style={{textAlign:'center', padding:24, color:'var(--text-muted)'}}>No votes this week</td></tr>
                      ) : botwVotes.map(v => (
                        <tr key={v.id}>
                          <td style={{fontSize:11, fontFamily:'monospace'}}>{v.submission_id?.slice(0,8)}...</td>
                          <td style={{fontSize:11, fontFamily:'monospace'}}>{v.user_id?.slice(0,8)}...</td>
                          <td>{new Date(v.created_at).toLocaleDateString()}</td>
                          <td className="td-actions"><button className="admin-btn admin-btn-danger" onClick={() => deleteBotwVote(v.id)}>Delete</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
